/**
 * Composant Informations - Présentation de l'entreprise
 * Utilise variablesData passé en props depuis le template
 */

import {
  type DocumentComponent,
  type DocumentProps,
  type VariablesData,
} from "../../lib/types";
import { Building2, Calendar, MapPin, Users } from "lucide-react";
import { createElement } from "react";
import { Badge, Card, CardContent, CardHeader, CardTitle, Separator } from "@repo/ui";

function KeyFact({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-controle bg-muted">
        {icon}
      </div>
      <div>
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground">{value}</p>
      </div>
    </div>
  );
}

function DepartmentSection({
  title,
  description,
  subsections,
}: {
  title: string;
  description: string;
  subsections?: { name: string; items: string[] }[];
}) {
  return (
    <Card className="gap-0">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardHeader>
      {subsections && subsections.length > 0 && (
        <CardContent className="space-y-3 pt-0">
          {subsections.map((sub) => (
            <div key={sub.name}>
              <Badge variant="secondary" className="mb-1.5">
                {sub.name}
              </Badge>
              <ul className="ml-4 space-y-0.5">
                {sub.items.map((item, i) => (
                  <li
                    key={i}
                    className="text-sm text-muted-foreground before:mr-2 before:content-['•']"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}

function Presentation({ variablesData }: { variablesData: VariablesData }) {
  const todayDate = new Date();
  const lastYear = todayDate.getFullYear() - 1;

  return (
    <div className="space-y-8">
      {/* La Situation */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">La Situation</h3>
        <div className="space-y-3 text-sm leading-relaxed text-foreground/90">
          <p>
            Vous êtes{" "}
            <span className="font-semibold">
              {variablesData.responsable_grand_est_actuel_prenom}{" "}
              {variablesData.responsable_grand_est_actuel_nom}
            </span>
            , et vous allez assurer l'interim du poste de{" "}
            <span className="font-semibold">
              {variablesData.responsable_grand_est_actuel_fonction}
            </span>{" "}
            de la société{" "}
            <span className="font-semibold">
              {variablesData.responsable_grand_est_actuel_societe}
            </span>
            , en remplacement de{" "}
            <span className="font-semibold">
              {variablesData.responsable_grand_est_precedent_prenom}{" "}
              {variablesData.responsable_grand_est_precedent_nom}
            </span>
            , absent pour longue maladie.
          </p>
          <p>
            Cette mutation, pour un intérim de trois mois a été décidée par{" "}
            <span className="font-semibold">
              {variablesData.presidente_CA_prenom}{" "}
              {variablesData.presidente_CA_nom}
            </span>{" "}
            {variablesData.presidente_CA_fonction} et{" "}
            <span className="font-semibold">
              {variablesData.directeur_general_prenom}{" "}
              {variablesData.directeur_general_nom}
            </span>{" "}
            {variablesData.directeur_general_fonction}. Vous avez carte blanche
            pour prendre les décisions qui s'imposent et apporter vos
            compétences managériales au développement de la société. Vous savez
            que vos dirigeants sont sensibles à l'esprit entrepreneurial et à
            l'innovation.
          </p>
          <p>
            Aujourd'hui, vous entrez dans vos nouvelles fonctions et plusieurs
            situations-problèmes nécessitent votre attention.{" "}
            <span className="font-semibold">
              {variablesData.secretaire_prenom} {variablesData.secretaire_nom}
            </span>
            , votre secrétaire, a déposé sur votre environnement numérique de
            travail un ensemble d'informations concernant la société, l'agenda
            de {variablesData.responsable_grand_est_precedent_prenom}{" "}
            {variablesData.responsable_grand_est_precedent_nom}, le budget
            prévisionnel ainsi qu'un certain nombre de mails.
          </p>
        </div>
      </section>

      <Separator />

      {/* Présentation entreprise */}
      <section className="space-y-5">
        <div className="flex items-center gap-4">
          <img
            src={
              variablesData.sytel_grand_est_logo ||
              "/logos/sytel_grand_est.png"
            }
            alt="Logo entreprise"
            className="h-16 w-16 rounded-surface object-contain"
          />
          <h3 className="text-lg font-semibold text-foreground">
            Présentation de {variablesData.societe_nom}
          </h3>
        </div>

        {/* Key facts grid */}
        <div className="grid grid-cols-2 gap-4">
          <KeyFact
            icon={<MapPin className="h-4 w-4 text-muted-foreground" />}
            label="Siège social"
            value="102 Allée du Plein-Soleil, 54 000 NANCY"
          />
          <KeyFact
            icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
            label="Création"
            value={`1990 par Maurice LEGRAND`}
          />
          <KeyFact
            icon={<Users className="h-4 w-4 text-muted-foreground" />}
            label="Effectif global"
            value="~300 personnes (3 régions × ~100)"
          />
          <KeyFact
            icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
            label={`CA HT ${lastYear}`}
            value="35 M€ (total) / 12 M€ (Grand Est)"
          />
        </div>

        {/* Company details */}
        <Card className="gap-0">
          <CardContent className="space-y-3 pt-6 text-sm leading-relaxed text-foreground/90">
            <p>
              <span className="font-medium">Activité :</span>{" "}
              {variablesData.societe_mere_nom} fabrique et vend de nombreux
              produits en matière plastique : de l'armoire de brassage
              électrique à la façade de téléphone portable, en passant par les
              boîtiers de prises téléphoniques ou les gaines de fils électriques
              ; soit au total plus de 100 références.
            </p>
            <p>
              <span className="font-medium">Effectif :</span> la société a
              connu une évolution fulgurante les 3 dernières années, passant de
              10 à 300 personnes. Les effectifs sont répartis dans 3 grandes
              régions (chaque région tourne avec un effectif d'environ 100
              personnes). La structure organisationnelle est identique pour
              chaque région (5 départements : commercial, administration, études
              et procédés, production, entretien et sécurité). La moyenne d'âge
              est de 34,5 ans.
            </p>
            <p>
              <span className="font-medium">Organisation :</span> mise en place
              par son fondateur, et compte-tenu de l'évolution rapide des
              premières années, l'organisation a visé à doter{" "}
              {variablesData.societe_mere_nom} d'un outil de production très
              flexible, qui permet de réaliser 2 types de fabrication :
            </p>
            <ul className="ml-4 space-y-0.5">
              <li className="text-sm before:mr-2 before:content-['•']">
                les grandes séries
              </li>
              <li className="text-sm before:mr-2 before:content-['•']">
                les petites séries et le développement de nouveaux produits
              </li>
            </ul>
            <p>
              Les temps de travail : l'horaire hebdomadaire est de 35 H, avec
              annualisation pour les cadres ; les agents de fabrication
              travaillent en équipes de 2 × 8H et récupèrent une journée par
              quinzaine.
            </p>
            <p>
              <span className="font-medium">Marché :</span> l'adaptabilité à la
              demande ponctuelle et le service « sur mesure » est devenue
              l'avantage concurrentiel de {variablesData.societe_mere_nom} ;
              elle est également à l'origine de sa croissance régulière. Les
              clients sont à la fois des PME de la région (bâtiment,
              électricité, télécommunications, …) mais aussi des multinationales
              qui proposent des contrats de sous-traitance.
            </p>
            <p>
              Aujourd'hui, le développement de la téléphonie mobile oblige{" "}
              {variablesData.societe_mere_nom} et particulièrement la Région
              Grand Est à s'agrandir et se réinventer.
            </p>
          </CardContent>
        </Card>
      </section>

      <Separator />

      {/* Mission des départements */}
      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">
          Mission des départements
        </h3>

        <div className="space-y-3">
          <DepartmentSection
            title="Production"
            description="Donner à la fabrication des grandes séries les moyens de produire dans les meilleures conditions, à un meilleur prix de revient et au niveau de qualité requis."
            subsections={[
              {
                name: "Achats",
                items: [
                  "Réaliser le cahier des charges et lancer les demandes d'offres",
                  "Choisir les fournisseurs en fonction de critères rigoureux : prix, qualité, délai d'approvisionnement",
                  "Mesurer la qualité des fournisseurs par des audits réguliers",
                ],
              },
              {
                name: "Logistique",
                items: [
                  "Assurer les transports internes et les expéditions",
                  "Mettre en place une gestion des stocks",
                  "Analyser et résoudre les problèmes de stocks, de consommations anormales, de livraisons",
                  "Veiller au respect du niveau des stocks de sécurité",
                ],
              },
              {
                name: "Fabrication",
                items: [
                  "Établir les plannings et coordonner les équipes",
                  "Réaliser les pièces selon le cahier des charges",
                  "Respecter les temps prédéfinis",
                ],
              },
            ]}
          />

          <DepartmentSection
            title="Études et Procédés"
            description="Piloter les process, le changement technologique, le développement de nouveaux produits."
            subsections={[
              {
                name: "Missions",
                items: [
                  "Piloter le bureau d'études",
                  "Définir les temps et moyens de production",
                  "Gérer le lancement des pré-séries et échantillons initiaux",
                  "Réaliser le suivi des coûts du process",
                  "Rédiger les rapports d'échantillons initiaux concernant les modifications des produits et suivre l'acceptation du client",
                  "Contribuer à l'enrichissement du portefeuille « brevets »",
                  "Entretenir une veille technologique",
                ],
              },
            ]}
          />

          <DepartmentSection
            title="Service Commercial"
            description="Élaborer la stratégie commerciale de l'entreprise."
            subsections={[
              {
                name: "Missions",
                items: [
                  "Établir et suivre le budget annuel (volume, CA, prix)",
                  "Négocier de nouvelles commandes",
                  "Maintenir un contact permanent avec les clients pour connaître leurs projets et proposer des solutions adéquates",
                  "Rédiger des offres attractives",
                  "Suivre l'exécution des contrats et le développement de la qualité des services clients",
                  "Entretenir une veille concurrentielle",
                ],
              },
            ]}
          />

          <DepartmentSection
            title="Entretien et Sécurité"
            description="Entretenir l'outil de fabrication (bâtiments, machines, énergies) pour une utilisation optimale en toute sécurité."
            subsections={[
              {
                name: "Missions",
                items: [
                  "Planifier et faire exécuter les travaux de maintenance préventive",
                  "Déclencher l'intervention lors des pannes",
                  "Analyser les coûts d'exploitation et d'entretien",
                  "Être le garant permanent de l'application des normes de sécurité",
                  "Assurer un rôle de formation auprès du personnel",
                  "Élaborer les indicateurs et les statistiques de facteurs de risques",
                  "Rédiger les règles générales et temporaires préventives",
                  "Superviser la gestion des déchets industriels",
                ],
              },
            ]}
          />

          <DepartmentSection
            title="Administration"
            description="Coordonner et superviser les services administratifs."
            subsections={[
              {
                name: "Personnel",
                items: [
                  "Gérer les temps de travail et la paie",
                  "Établir le diagnostic social",
                  "Entretenir les relations avec les partenaires internes et externes",
                  "Établir les plans de formation",
                ],
              },
              {
                name: "Comptabilité",
                items: [
                  "Assurer les rôles de comptabilité générale, analytique et le contrôle de gestion",
                  "Gérer la facturation et le recouvrement",
                  "Préparer, rédiger et diffuser les budgets à court et moyen terme",
                  "Satisfaire aux obligations légales",
                ],
              },
              {
                name: "Informatique",
                items: [
                  "Élaborer le schéma directeur informatique",
                  "Suivre les projets menés avec les autres services",
                  "Réaliser les dépannages bureautiques et la maintenance du parc",
                ],
              },
            ]}
          />
        </div>
      </section>
    </div>
  );
}

/**
 * Composant Informations avec métadonnées
 */
export const Informations: DocumentComponent = function Informations({
  variablesData,
}: DocumentProps) {
  return createElement(Presentation, { variablesData });
};

Informations.metadata = {
  id: "informations",
  titre: "Informations entreprise",
  icon: "info",
};

export default Informations;
